import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiZap, FiCompass, FiTarget, FiRefreshCw } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { getQuizHistory, checkCanRetake, getMyAssessment } from "../api/client";
// UserHistoryWidget intentionally removed from the welcome screen for now —
// the component still exists in ../components/UserHistoryWidget.jsx and its
// /api/quiz/my-progress endpoint is still live, so dropping it back in is a
// one-line re-import when we're ready to expose it again.

export default function Welcome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quizHistory, setQuizHistory] = useState(null);
  const [retakeInfo, setRetakeInfo] = useState(null);
  const [assessment, setAssessment] = useState(null); // { id, name, ... }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const [historyRes, retakeRes, assessmentRes] = await Promise.all([
          getQuizHistory(),
          checkCanRetake(),
          // Fetch the user's currently-assigned assessment so the button
          // can display its name. Failure is non-fatal — we'll fall back to
          // the generic "Assessment" wording.
          getMyAssessment().catch(() => ({ data: null })),
        ]);

        const sessions = historyRes.data || [];
        const completed = sessions.find((s) => s.is_completed);
        const inProgress = sessions.find((s) => !s.is_completed);
        setQuizHistory({ completed, in_progress: inProgress, allSessions: sessions });
        setRetakeInfo(retakeRes.data || { can_retake: false });
        setAssessment(assessmentRes.data || null);
      } catch (err) {
        console.error("Failed to fetch quiz status:", err);
        setQuizHistory(null);
        setRetakeInfo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";

  const getTimeGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const hasCompleted = !loading && quizHistory?.completed;
  const hasInProgress = !loading && quizHistory?.in_progress;
  const canRetake = !loading && retakeInfo?.can_retake;
  const attemptCount = quizHistory?.allSessions?.filter((s) => s.is_completed).length || 0;
  // Pretty name for whichever assessment is currently assigned.
  const assessmentName = assessment?.name || "Assessment";

  return (
    <div className="welcome-page">
      <motion.div
        className="welcome-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="greeting"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h1>{getTimeGreeting()}, <span className="highlight-name">{firstName}</span></h1>
          <p className="greeting-sub">
            Welcome to the starting line. Your <strong>AIQ</strong> maps where you are today,
            so you can see exactly how far you'll go.
          </p>
        </motion.div>

        <motion.div
          className="info-cards"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="info-card">
            <FiZap className="info-icon" />
            <p>Questions adjust to your knowledge level in real-time</p>
          </div>
          <div className="info-card">
            <FiCompass className="info-icon" />
            <p>Real-world scenarios across all AI domains — to challenge and reveal</p>
          </div>
          <div className="info-card">
            <FiTarget className="info-icon" />
            <p>Walk away knowing your strengths and your next move</p>
          </div>
        </motion.div>

        {!loading && (
          <motion.div
            className="welcome-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            {hasInProgress ? (
              /* In-progress session — resume */
              <motion.button
                className="btn-primary btn-large"
                style={{ marginTop: "12px" }}
                onClick={() => navigate("/rules")}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FiZap /> Resume {assessmentName}
              </motion.button>
            ) : hasCompleted && canRetake ? (
              /* Completed but retake granted — allow retake */
              <>
                <motion.button
                  className="btn-primary btn-large"
                  style={{ marginTop: "12px" }}
                  onClick={() => navigate("/rules")}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiRefreshCw /> Retake {assessmentName}
                </motion.button>
                <p className="completed-notice" style={{ color: "var(--color-highlight)" }}>
                  A retake has been granted by your administrator.
                  {retakeInfo.reason ? ` Reason: ${retakeInfo.reason}` : ""}
                  {attemptCount > 0 && ` (Attempt ${attemptCount + 1})`}
                </p>
              </>
            ) : hasCompleted ? (
              /* Completed, no retake available — terminal state, button stays
                 disabled. Users without an admin-granted retake shouldn't be
                 able to start a new attempt; the message tells them why. */
              <>
                <motion.button
                  className="btn-primary btn-large btn-disabled"
                  disabled
                >
                  <FiZap /> {assessmentName} Completed
                </motion.button>
                <p className="completed-notice">
                  Thank you for taking the AIQ Assessment. Your results are with the administrator.
                  {attemptCount > 1 && ` (${attemptCount} attempts completed)`}
                </p>
              </>
            ) : (
              /* No session yet — begin */
              <motion.button
                className="btn-primary btn-large"
                style={{ marginTop: "12px" }}
                onClick={() => navigate("/rules")}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FiZap /> Begin {assessmentName}
              </motion.button>
            )}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
